import { useUnit } from "effector-react"
import { t } from "i18next"
import { Icon, Tag } from "@callcenter/design-system"
import { $userService, $callcenterService } from "@callcenter/effector-services"

import type { MenuGroup } from "../types"

// eslint-disable-next-line complexity
export const useMenuItems = (): Array<MenuGroup> => {
  const permissions = useUnit($userService.$permissions)
  const callcenterPermissions = useUnit($callcenterService.$permissions)
  const userSettings = useUnit($userService.$userSettings)
  const callcenterSettings = useUnit($callcenterService.$settings)

  return [
    {
      label: t("mainMenuBar_reportingMenu_mainMenuLabel_reportingMenuLabel"),
      key: "reporting",
      children: [
        {
          label: t("mainMenuBar_reportingMenu_reportingOptionLabel_callDetailRecordsLabel"),
          path: "/cdr",
          isVisible: permissions.has("view_cdrs")
        },
        {
          label: t("mainMenuBar_reportingMenu_reportingOptionLabel_historicalReportsLabel"),
          path: "/reports",
          isVisible: permissions.has("view_historical_reports")
        },
        {
          label: t("mainMenuBar_reportingMenu_reportingOptionLabel_realtimeDashboardLabel"),
          path: "/metrics",
          isVisible: permissions.has("view_metrics")
        }
      ]
    },
    {
      label: t("mainMenuBar_inboundMenu_mainMenuLabel_inboundMenuLabel"),
      key: "inbound",
      children: [
        {
          label: t("mainMenuBar_inboundMenu_inboundOptionLabel_numbersLabel"),
          path: "/numbers/my",
          isVisible: permissions.has("buy_numbers") || permissions.has("edit_numbers")
        },
        {
          label: t("mainMenuBar_inboundMenu_inboundOptionLabel_queuesLabel"),
          path: "/lines",
          isVisible:
            permissions.has("create_queues") || permissions.has("edit_queues") || permissions.has("edit_own_queues")
        },
        {
          label: t("mainMenuBar_inboundMenu_inboundOptionLabel_flowsLabel"),
          path: "/flows",
          isVisible: permissions.has("view_flows") || permissions.has("edit_flows")
        },
        {
          label: t("mainMenuBar_inboundMenu_inboundOptionLabel_blockedNumbersLabel"),
          path: "/blocked_inbound_clis",
          isVisible: permissions.has("edit_blocked_inbound_clis")
        },
        {
          label: t("mainMenuBar_inboundMenu_inboundOptionLabel_allowedVoiceTrunkIPsLabel"),
          path: "/inbound_allowed_ips",
          isVisible: callcenterPermissions.has("inbound_allowed_ips") && permissions.has("view_inbound_allowed_ips")
        }
      ]
    },
    {
      label: t("mainMenuBar_outboundMenu_mainMenuLabel_outboundMenuLabel"),
      key: "outbound",
      children: [
        {
          label: t("mainMenuBar_outboundMenu_outboundOptionLabel_dialerCampaignsLabel"),
          path: "/campaigns",
          isVisible: permissions.has("edit_campaigns") || permissions.has("edit_own_campaigns")
        },
        {
          label: t("mainMenuBar_outboundMenu_outboundOptionLabel_callerIDGroupsLabel"),
          path: "/cid_groups",
          isVisible: permissions.has("view_cid_groups")
        },
        {
          label: t("mainMenuBar_outboundMenu_outboundOptionLabel_numberValidatorLabel"),
          path: "/number_validator",
          isVisible: permissions.has("allow_number_validation")
        },
        {
          label: t("mainMenuBar_outboundMenu_outboundOptionLabel_callerIDRiskCheckLabel"),
          path: "/risk_checks",
          isVisible: permissions.has("use_risk_checks")
        },
        {
          label: t("mainMenuBar_outboundMenu_prefixOverrideOptionLabel_prefixOverrideLabel"),
          path: "/number_prefix_overrides",
          isVisible: permissions.has("edit_callcenters") && callcenterSettings.number_prefix_override_enabled
        },
        {
          label: t("mainMenuBar_outboundMenu_outboundOptionLabel_callSuppressionLabel"),
          path: permissions.has("edit_hardcaps")
            ? "/call_suppression/suppressed_numbers"
            : "/call_suppression/exceptions",
          isVisible: permissions.has("edit_hardcaps") || permissions.has("edit_hardcap_exceptions")
        },
        {
          label: t("mainMenuBar_outboundMenu_dncrOptionLabel_dncrLabel"),
          path: "/dncr",
          isVisible: permissions.has("edit_dncr")
        }
      ]
    },
    {
      label: t("mainMenuBar_usersMenu_mainMenuLabel_usersMenuLabel"),
      key: "users",
      children: [
        {
          label: t("mainMenuBar_usersMenu_usersOptionLabel_usersLabel"),
          path: "/users",
          isVisible: permissions.has("view_users")
        },
        {
          label: t("mainMenuBar_usersMenu_teamsOptionLabel_teamsLabel"),
          path: "/teams",
          isVisible: permissions.has("view_teams")
        },
        {
          label: t("mainMenuBar_usersMenu_skillsOptionLabel_skillsLabel"),
          path: "/skills",
          isVisible: permissions.has("edit_skills")
        }
      ]
    },
    {
      label: t("mainMenuBar_aiAgentsMenu_mainMenuLabel_aiAgentsMenuLabel"),
      key: "virtual-users",
      icon: <Icon size={16} color="var(--vds-gray-10)" icon={Icon.icons.system.s.utilities.sparkle} />,
      children: [
        {
          label: t("mainMenuBar_aiAgentsMenu_aiAgentsOptionLabel_aiAgentsLabel"),
          path: "/virtual_users",
          isVisible: permissions.has("view_users") && callcenterPermissions.has("byob"),
          badge: <Tag kind="additional">{t("mainMenuBar_aiAgentsMenu_aiAgentsOptionLabel_betaLabel")}</Tag>
        },
        {
          label: t("mainMenuBar_aiAgentsMenu_knowledgeBasesOptionLabel_knowledgeBasesLabel"),
          path: "/knowledge_bases",
          isVisible: permissions.has("view_users") && callcenterPermissions.has("byob")
        }
      ]
    },
    {
      label: t("mainMenuBar_administrationMenu_mainMenuLabel_administrationMenuLabel"),
      key: "administration",
      children: [
        {
          label: t("mainMenuBar_administrationMenu_systemSettingsOptionLabel_systemSettingsLabel"),
          path: "/settings/edit",
          isVisible: permissions.has("edit_callcenters")
        },
        {
          label: t("mainMenuBar_administrationMenu_systemSettingsOptionLabel_voiceProvidersLabel"),
          path: "/voice_providers",
          isVisible: permissions.has("edit_byoc_providers")
        },
        {
          label: t("mainMenuBar_administrationMenu_webhooksOptionLabel_webhooksLabel"),
          path: "/webhooks",
          isVisible: permissions.has("view_webhooks")
        },
        {
          label: t("mainMenuBar_administrationMenu_billingOptionLabel_billingLabel"),
          path: "/billing/account",
          isVisible:
            permissions.has("edit_billing") && (callcenterPermissions.has("billing_section") || userSettings.is_support)
        },
        {
          label: t("mainMenuBar_administrationMenu_securityAccessGroupsOptionLabel_securityAccessGroupsLabel"),
          path: "/access_control_lists",
          isVisible: permissions.has("edit_acls")
        },
        {
          label: t("mainMenuBar_administrationMenu_digitalChannelsOptionLabel_digitalChannelsLabel"),
          path: "/digital_channels",
          isVisible: permissions.has("view_digital_channels")
        },
        {
          label: t("mainMenuBar_administrationMenu_apiKeysOptionLabel_apiKeysLabel"),
          path: "/api_keys",
          isVisible: permissions.has("view_api_keys")
        },
        {
          label: t("mainMenuBar_administrationMenu_crmIntegrationSettingsOptionLabel_crmIntegrationSettingsLabel"),
          path: "/integrations",
          isVisible: permissions.has("view_integrations")
        },
        {
          label: t("mainMenuBar_administrationMenu_interactionContextKeysOptionLabel_interactionContextKeysLabel"),
          path: "/interaction_context_keys",
          isVisible: permissions.has("view_interaction_context_keys")
        },
        {
          label: t("mainMenuBar_administrationMenu_allowedHostsSubnetsOptionLabel_allowedHostsSubnetsLabel"),
          path: "/allowed_ips",
          isVisible: permissions.has("view_allowed_ips")
        },
        {
          label: t("mainMenuBar_administrationMenu_mediaOptionLabel_mediaLabel"),
          path: "/media_files",
          isVisible: permissions.has("view_media_files")
        },
        {
          label: t("mainMenuBar_administrationMenu_unavailableCodesOptionLabel_unavailableCodesLabel"),
          path: "/statuses",
          isVisible: permissions.has("edit_statuses")
        },
        {
          label: t("mainMenuBar_administrationMenu_wrapupCodesOptionLabel_wrapupCodesLabel"),
          path: "/dispatch_codes",
          isVisible: permissions.has("edit_dispatch_codes")
        },
        {
          label: t("mainMenuBar_administrationMenu_topicsOptionLabel_topicsLabel"),
          path: "/topics",
          isVisible: permissions.has("view_topics")
        },
        {
          label: t("mainMenuBar_administrationMenu_keywordGroupsOptionLabel_keywordGroupsLabel"),
          path: "/keyword_groups",
          isVisible: permissions.has("view_keyword_groups") && callcenterSettings.has_keyword_group_supported_languages
        },
        {
          label: t("mainMenuBar_administrationMenu_messageTemplatesOptionLabel_messageTemplatesLabel"),
          path: permissions.has("view_canned_messages")
            ? "/message_templates/canned_messages"
            : "/message_templates/whatsapp_templates",
          isVisible: permissions.has("view_canned_messages") || permissions.has("view_whatsapp_templates")
        },
        {
          label: t("mainMenuBar_administrationMenu_senderIDGroupsOptionLabel_senderIDGroupsLabel"),
          path: "/sender_ids",
          isVisible: permissions.has("view_sender_ids")
        },
        {
          label: t("mainMenuBar_administrationMenu_deleteRecordingsOptionLabel_deleteRecordingsLabel"),
          path: "/call_recordings",
          isVisible: permissions.has("delete_call_recordings")
        }
      ]
    },
    {
      label: t("mainMenuBar_messagingMenu_mainMenuLabel_messagingMenuLabel"),
      key: "messaging",
      children: [
        {
          label: t("mainMenuBar_messagingMenu_broadcastMessagesOptionLabel_broadcastMessagesLabel"),
          path: "/notifications",
          isVisible: permissions.has("access_broadcast_messages")
        }
      ]
    }
  ]
    .map((group) => ({ ...group, children: group.children.filter((item) => item.isVisible) }))
    .filter((group) => group.children.length > 0)
}
